// eslint-disable-next-line @glovo/custom-rules/enforce-glovo-rum
import type {
  RumErrorEvent,
  RumErrorEventDomainContext,
  RumEvent,
  RumEventDomainContext,
} from '@datadog/browser-rum-slim'
// eslint-disable-next-line no-restricted-imports
import { cloneDeep } from 'lodash'
import { ignoredErrors } from '../../ignored-errors'
import {
  getApiErrorDetails,
  getErrorMessage,
  getStaticCode,
  getStatusCode,
  isAxiosError,
} from '@/tools/error-handling'
import { removeSensitiveData } from '@/tools/o11y/helpers/remove-sensitive-data/remove-sensitive-data'
import { _getRouteNameClientSide } from '@/tools/route-names'

const UNKNOWN = '__UNKNOWN__'
const SECRET = '__SECRET__'

export function onBeforeSend(event: RumEvent, context: RumEventDomainContext) {
  try {
    event.view.name = _getRouteNameClientSide()
    if (isErrorEvent(event)) {
      enrichErrorEventContext(event as RumErrorEvent, context as RumErrorEventDomainContext)
    }
    removeSensitiveData(event)
    // eslint-disable-next-line @glovo/custom-rules/always-forward-error, @typescript-eslint/no-unused-vars
  } catch (e) {
    // This try/catch block prevents the event processing from breaking the monitoring
  }
  return shouldSendEvent(event)
}

export function mapErrorContext<T extends object>(context: T, error: unknown): T {
  const errorDetails: Record<PropertyKey, unknown> = {}

  const hasErrorName = error && typeof error === 'object' && 'name' in error
  errorDetails.type = hasErrorName ? error.name : UNKNOWN

  if (isAxiosError(error)) {
    errorDetails.code = error.code || UNKNOWN
    errorDetails.url = error.config?.url || UNKNOWN
    errorDetails.method = error.config?.method || UNKNOWN
    errorDetails.message = error.message || UNKNOWN
    errorDetails.statusCode = getStatusCode(error) || UNKNOWN
    errorDetails.staticCode = getStaticCode(error) || UNKNOWN
    errorDetails.apiErrorDetails = getApiErrorDetails(error) || UNKNOWN
    errorDetails.headers = getRequestHeadersFromNetworkError(error)
  } else if (error instanceof Error) {
    for (const errorProperty of Object.getOwnPropertyNames(error)) {
      if (errorProperty === 'errorBoundaryContext') {
        continue
      }
      errorDetails[errorProperty] = error[errorProperty as keyof typeof error]
    }
  }

  return {
    ...context,
    ...getErrorBoundaryContext(error),
    errorDetails: {
      ...('errorDetails' in context && typeof context.errorDetails === 'object'
        ? context.errorDetails
        : {}),
      ...errorDetails,
    },
  }
}

export function stringifiedError(error: unknown): string {
  if (isAxiosError(error)) {
    return `${getErrorMessage(error)} [${getStatusCode(error)}]`
  } else if (error instanceof Error) {
    return error.message || UNKNOWN
  } else {
    return error && typeof error === 'object' ? JSON.stringify(error) : getErrorMessage(error)
  }
}

export function enrichErrorEventContext(event: RumErrorEvent, context: RumErrorEventDomainContext) {
  event.context = event.context || {}
  try {
    const error = context.error
    const errorStr = stringifiedError(error)

    event.context = mapErrorContext(event.context, error)
    event.error.message = errorStr
    event.context.errorMessage = errorStr
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @glovo/custom-rules/always-forward-error
  } catch (e) {
    event.context.enrichErrorFailed = true
  }
}

export function shouldSendEvent(event: RumEvent): boolean {
  if (!isErrorEvent(event)) {
    return true
  }

  const message = event.error.message
  for (const rule of ignoredErrors) {
    const matchesRule = rule instanceof RegExp ? rule.test(message) : message === rule
    if (matchesRule) {
      return false
    }
  }

  // It seems GSI (Google Sign In) is rejecting a promise with `undefined`  that is introducing
  // a lot of noise in our O11Y. This next line ignores that rejection.
  if (String(event.error.handling_stack).includes('gsi/client')) {
    // allow list errors coming from Google sign-in client
    return false
  }

  return true
}

export function getNextHopProtocol(): string | undefined {
  const perf = performance?.getEntries() || []
  const nextHopProtocol = isPerformanceResourceTiming(perf[0])
    ? perf[0].nextHopProtocol || ''
    : undefined
  return nextHopProtocol
}

function getRequestHeadersFromNetworkError(
  error: unknown
): Record<PropertyKey, unknown> | undefined {
  if (!isAxiosError(error)) {
    return
  }
  const headers: Record<PropertyKey, unknown> = cloneDeep(error.config?.headers || {})
  if ('Authorization' in headers) {
    headers.Authorization = SECRET
  }
  return headers
}

function isPerformanceResourceTiming(entry?: PerformanceEntry): entry is PerformanceResourceTiming {
  return typeof entry === 'object' && entry !== null && 'nextHopProtocol' in entry
}

function isErrorEvent(event: RumEvent): event is RumErrorEvent {
  return event.type === 'error'
}

function getErrorBoundaryContext(error: unknown): object {
  if (
    error instanceof Object &&
    'errorBoundaryContext' in error &&
    error.errorBoundaryContext &&
    typeof error.errorBoundaryContext === 'object'
  ) {
    return error.errorBoundaryContext
  }
  return {}
}
